import React, { useEffect, useState, useMemo } from "react";
import axiosClient from "../api/axiosClient";
import {
  Table,
  Spinner,
  Alert,
  Card,
  Row,
  Col,
  Form,
} from "react-bootstrap";

import { Pie, Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ChartDataLabels
);

// GLASSMORPHISM
const glass = {
  background: "rgba(255, 255, 255, 0.25)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
  border: "1px solid rgba(255, 255, 255, 0.3)",
  boxShadow: "0 8px 25px rgba(0,0,0,0.08)",
  borderRadius: 16,
};

// CARD HEIGHTS
const SUMMARY_HEIGHT = 150;
const CHART_HEIGHT = 420;

// PIE CHART COLORS
const COLORS = [
  "#FF6384",
  "#36A2EB",
  "#FFCE56",
  "#4BC0C0",
  "#9966FF",
  "#FF9F40",
  "#7FB77E",
  "#E15FED",
  "#76D7C4",
  "#F1948A",
];

// Helpers
const findKey = (obj, candidates) => {
  if (!obj) return null;
  const keys = Object.keys(obj);

  for (const cand of candidates) {
    const norm = cand.toLowerCase().replace(/\s|_/g, "");
    const match = keys.find(
      (k) => k.toLowerCase().replace(/\s|_/g, "") === norm
    );
    if (match) return match;
  }
  return null;
};

const parseNumber = (v) => {
  if (v == null) return 0;
  return parseFloat(String(v).replace(/[^0-9.-]/g, "")) || 0;
};

const parseDate = (v) => {
  if (!v) return null;

  if (v instanceof Date && !isNaN(v)) return v;
  if (typeof v === "number") return new Date(v);

  const match = String(v).match(/\/Date\((\d+)\)\//);
  if (match) return new Date(Number(match[1]));

  let d = new Date(String(v).trim());
  if (!isNaN(d)) return d;

  d = new Date(String(v).replace(" ", "T"));
  return !isNaN(d) ? d : null;
};

const toLocalDateOnly = (d) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate());

const formatCurrency = (v) =>
  "₱" + Number(v).toLocaleString("en-US", { minimumFractionDigits: 2 });

const formatNumber = (v) => Number(v).toLocaleString("en-US");


//
// =============================== COMPONENT ===============================
//

const VIPManualAnalytics = ({ currentUser }) => {
  const [rawRows, setRawRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selectedStore, setSelectedStore] = useState("All");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [topN, setTopN] = useState(10);

  const [detectedKeys, setDetectedKeys] = useState({});
  const [dateKey, setDateKey] = useState(null);

  //
  // ----- LOAD DATA -----
  //
  useEffect(() => {
    if (!currentUser?.userId) {
      setError("User not identified.");
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);

        const res = await axiosClient.get(
          `/manual-reports/vip/${currentUser.userId}?report_type=consumption_by_medicine`
        );

        const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setRawRows(data);

        if (data.length > 0) {
          const sample = data[0];

          const storeKey = findKey(sample, ["Store", "Store_Code", "Store Name"]);
          const descKey = findKey(sample, ["Description", "Desc", "Item"]);
          const uomKey = findKey(sample, ["Unit_of_Measure", "UOM"]);
          const qtyKey = findKey(sample, ["Quantity", "Qty"]);
          const amountKey = findKey(sample, ["Total_Amount", "Amount"]);
          const detectedDateKey = findKey(sample, [
            "Dispense_Date",
            "Dispense Date",
            "Date",
          ]);

          setDetectedKeys({
            storeKey,
            descKey,
            uomKey,
            qtyKey,
            amountKey,
          });

          setDateKey(detectedDateKey);

          // Default last 30 days
          const dates = data
            .map((r) => parseDate(r[detectedDateKey]))
            .filter(Boolean)
            .sort((a, b) => a - b);

          if (dates.length) {
            const max = dates[dates.length - 1];
            const min = new Date(max);
            min.setDate(min.getDate() - 30);

            setStartDate(min.toISOString().split("T")[0]);
            setEndDate(max.toISOString().split("T")[0]);
          }
        }

        setLoading(false);
      } catch (err) {
        setError("Failed to load manual report.");
        setLoading(false);
      }
    };

    load();
  }, [currentUser]);

  const { storeKey, descKey, uomKey, qtyKey, amountKey } = detectedKeys;

  //
  // ----- STORE LIST -----
  //
  const allStores = useMemo(() => {
    if (!storeKey) return ["All"];
    const set = new Set(rawRows.map((r) => r[storeKey]).filter(Boolean));
    return ["All", ...Array.from(set).sort()];
  }, [rawRows, storeKey]);

  //
  // ----- FILTER -----
  //
  const filteredRows = useMemo(() => {
    let rows = rawRows;

    if (selectedStore !== "All")
      rows = rows.filter((r) => r[storeKey] === selectedStore);

    if (startDate && endDate) {
      const s = toLocalDateOnly(parseDate(startDate));
      const e = toLocalDateOnly(parseDate(endDate));

      rows = rows.filter((r) => {
        const d = parseDate(r[dateKey]);
        if (!d) return false;
        const dd = toLocalDateOnly(d);
        return dd >= s && dd <= e;
      });
    }
    return rows;
  }, [rawRows, selectedStore, startDate, endDate, storeKey, dateKey]);

  //
  // ----- PARSED -----
  //
 const parsed = useMemo(
  () =>
    filteredRows.map((r) => ({
      store: r[storeKey],
      desc: r[descKey],
      uom: r[uomKey],
      qty: parseNumber(r[qtyKey]),
      amount: parseNumber(r[amountKey]),
      raw: r,
    })),
  [filteredRows, storeKey, descKey, uomKey, qtyKey, amountKey]
);


  //
  // ----- SUMMARY -----
  //
  const transactionCount = parsed.length;
  const totalAmount = parsed.reduce((t, p) => t + p.amount, 0);

  const storeSummary = Object.values(
    parsed.reduce((acc, p) => {
      if (!acc[p.store]) acc[p.store] = { store: p.store, qty: 0, amount: 0 };
      acc[p.store].qty += p.qty;
      acc[p.store].amount += p.amount;
      return acc;
    }, {})
  );

  const highestQtyStore =
    storeSummary.sort((a, b) => b.qty - a.qty)[0] || { store: "-", qty: 0 };

  const highestAmountStore =
    storeSummary.sort((a, b) => b.amount - a.amount)[0] || {
      store: "-",
      amount: 0,
    };

  //
  // ----- DAILY SALES (DUAL AXIS LINE CHART) -----
  //
  const dailySales = useMemo(() => {
    const map = {};

    parsed.forEach((p) => {
      const d = parseDate(p.raw[dateKey]);
      if (!d) return;

      const iso = d.toISOString().split("T")[0];
      if (!map[iso]) map[iso] = { amount: 0, qty: 0 };

      map[iso].amount += p.amount;
      map[iso].qty += p.qty;
    });

    return Object.entries(map)
      .sort(([a], [b]) => new Date(a) - new Date(b))
      .map(([date, obj]) => ({
        date,
        label: new Date(date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
        amount: obj.amount,
        qty: obj.qty,
      }));
  }, [parsed, dateKey]);

  const lineData = {
    labels: dailySales.map((d) => d.label),
    datasets: [
      {
        label: "Amount (₱)",
        data: dailySales.map((d) => d.amount),
        borderColor: "#1E90FF",
        backgroundColor: "rgba(30,144,255,0.25)",
        yAxisID: "y",
        tension: 0.4,
        fill: true,
      },
      {
        label: "Quantity (#)",
        data: dailySales.map((d) => d.qty),
        borderColor: "#FF6384",
        backgroundColor: "rgba(255,99,132,0.25)",
        yAxisID: "y1",
        tension: 0.4,
        fill: true,
      },
    ],
  };

  //
  // ----- TOP AMOUNT -----
  //
  const topByAmount = [...parsed]
    .sort((a, b) => b.amount - a.amount)
    .slice(0, topN);

  const barAmountData = {
    labels: topByAmount.map((p) => p.desc || "Unknown"),
    datasets: [
      {
        data: topByAmount.map((p) => p.amount),
        backgroundColor: "#36A2EB80",
        borderColor: "#1E90FF",
        borderRadius: 8,
      },
    ],
  };

  //
  // ----- TOP QTY (PIE) -----
  //
  const topByQty = [...parsed]
    .sort((a, b) => b.qty - a.qty)
    .slice(0, topN);

  const pieData = {
    labels: topByQty.map((p) => p.desc || "Unknown"),
    datasets: [
      {
        data: topByQty.map((p) => p.qty),
        backgroundColor: COLORS,
      },
    ],
  };

  //
  // ----- EARLY RETURNS -----
  //
  if (loading)
    return (
      <div className="text-center my-5">
        <Spinner animation="border" />
      </div>
    );

  if (error) return <Alert variant="danger">{error}</Alert>;

  //
  // =============================== RENDER UI ===============================
  //
  return (
    <div>

      {/* ---------------- FILTER PANEL ---------------- */}
      <Card className="p-4 mb-4 shadow-sm" style={glass}>
        <h5 className="fw-bold mb-3">Filters</h5>

        <Row className="g-3 justify-content-center">
          <Col md={5}>
            <Form.Label>Store</Form.Label>
            <Form.Select
              value={selectedStore}
              onChange={(e) => setSelectedStore(e.target.value)}
            >
              {allStores.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </Form.Select>
          </Col>

          <Col md={3}>
            <Form.Label>Start Date</Form.Label>
            <Form.Control
              type="date"
              value={startDate}
              max={endDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Col>

          <Col md={3}>
            <Form.Label>End Date</Form.Label>
            <Form.Control
              type="date"
              value={endDate}
              min={startDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Col>
        </Row>
      </Card>

      {/* ---------------- SUMMARY CARDS ---------------- */}
      <Row className="g-4 mb-4 text-center">

        <Col md={4}>
          <Card
            className="p-3 shadow-sm h-100 d-flex flex-column justify-content-center"
            style={{ ...glass, minHeight: SUMMARY_HEIGHT }}
          >
            <h6>No. of Transactions</h6>
            <h2 className="fw-bold">{formatNumber(transactionCount)}</h2>
          </Card>
        </Col>

        <Col md={4}>
          <Card
            className="p-3 shadow-sm h-100 d-flex flex-column justify-content-center"
            style={{ ...glass, minHeight: SUMMARY_HEIGHT }}
          >
            <h6>Total Amount</h6>
            <h2 className="fw-bold">{formatCurrency(totalAmount)}</h2>
          </Card>
        </Col>

        <Col md={4}>
          <Card
            className="p-3 shadow-sm h-100 d-flex flex-column justify-content-center"
            style={{ ...glass, minHeight: SUMMARY_HEIGHT }}
          >
            <h6>Store Summary</h6>
            <p className="mb-1">
              <strong>Qty:</strong> {highestQtyStore.store} (
              {formatNumber(highestQtyStore.qty)})
            </p>
            <p className="mb-0">
              <strong>Amount:</strong> {highestAmountStore.store} (
              {formatCurrency(highestAmountStore.amount)})
            </p>
          </Card>
        </Col>

      </Row>

      {/* ---------------- 3 CHARTS (6 / 3 / 3) ---------------- */}
      <Row className="g-4">

        {/* Line Chart */}
        <Col md={6}>
          <Card
            className="p-3 shadow-sm h-100"
            style={{ ...glass, minHeight: CHART_HEIGHT }}
          >
            <h6 className="text-center mb-2">Sales Per Day</h6>

            <div style={{ height: CHART_HEIGHT }}>
              <Line
                data={lineData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: "bottom" },
                  },
                  scales: {
                    y: {
                      type: "linear",
                      position: "left",
                      ticks: {
                        callback: (v) => formatCurrency(v),
                      },
                    },
                    y1: {
                      type: "linear",
                      position: "right",
                      grid: { drawOnChartArea: false },
                      ticks: {
                        callback: (v) => Math.round(v), // NO DECIMALS
                      },
                    },
                  },
                }}
              />
            </div>
          </Card>
        </Col>

        {/* Top Amount Bar Chart */}
        <Col md={3}>
          <Card
            className="p-3 shadow-sm h-100"
            style={{ ...glass, minHeight: CHART_HEIGHT }}
          >
            <div className="d-flex justify-content-between mb-2">
              <h6>Top {topN} Amount</h6>
              <Form.Select
                size="sm"
                style={{ width: 80 }}
                value={topN}
                onChange={(e) => setTopN(Number(e.target.value))}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={15}>15</option>
                <option value={20}>20</option>
              </Form.Select>
            </div>

            <div style={{ height: CHART_HEIGHT }}>
              <Bar
                data={barAmountData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  indexAxis: "y",
                  plugins: { legend: { display: false } },
                  scales: {
                    x: {
                      ticks: { callback: (v) => formatCurrency(v) },
                    },
                  },
                }}
              />
            </div>
          </Card>
        </Col>

        {/* Pie Chart */}
        <Col md={3}>
          <Card
            className="p-3 shadow-sm h-100 d-flex flex-column align-items-center"
            style={{ ...glass, minHeight: CHART_HEIGHT }}
          >
            <h6 className="text-center mb-2">Top {topN} Quantity</h6>

            <div
              style={{
                height: CHART_HEIGHT + 60,
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Pie
                data={pieData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: "bottom",
                      labels: { font: { size: 12 } },
                    },
                  },
                }}
              />
            </div>
          </Card>
        </Col>

      </Row>

      {/* ---------------- TABLE ---------------- */}
      <Card className="shadow-sm mt-4" style={glass}>
        <Card.Body>
          <h5 className="fw-bold mb-3">Data Table</h5>

          <div style={{ maxHeight: 400, overflowY: "auto" }}>
            <div style={{ width: "100%", overflowX: "auto" }}>
              <Table hover size="sm" style={{ tableLayout: "fixed", width: "100%" }}>
                <thead
                  style={{
                    position: "sticky",
                    top: 0,
                    background: "rgba(255,255,255,0.55)",
                    backdropFilter: "blur(6px)",
                    zIndex: 3,
                  }}
                >
                  <tr>
                    {Object.keys(parsed[0]?.raw || {}).map((k) => (
                      <th
                        key={k}
                        style={{
                          width: 150,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {k}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {parsed.map((p, idx) => (
                    <tr key={idx}>
                      {Object.keys(p.raw).map((k) => {
                        const val = p.raw[k];
                        const lower = k.toLowerCase();

                        if (lower.includes("amount"))
                          return <td key={k}>{formatCurrency(parseNumber(val))}</td>;

                        if (lower.includes("qty"))
                          return <td key={k}>{formatNumber(parseNumber(val))}</td>;

                        return <td key={k}>{String(val)}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default VIPManualAnalytics;
